import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid
} from 'recharts';
import { getAuthHeader } from '../../helpers/auth';
import {
  TrendingUp, CheckCircle, Clock, Users, List,
  Eye, EyeOff, BarChart2, ChevronDown
} from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  surf2:   'var(--nd-surface-2)',
  surf3:   'var(--nd-surface-2)',
  border:  'var(--nd-w08)',
  bord2:   'var(--nd-w10)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx35)',
  faint:   'var(--nd-w10)',
  blue:    '#3B82F6',
  teal:    '#22D3A5',
  amber:   '#FBBF24',
  red:     '#F87171',
  purple:  '#A78BFA',
  green:   '#34D399',
  font:    "'DM Sans', sans-serif",
  mono:    "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

  .sd-stat-card { transition: transform 0.2s, box-shadow 0.2s; animation: fadeUp 0.35s ease both; }
  .sd-stat-card:hover { transform: translateY(-3px); box-shadow: 0 14px 32px rgba(0,0,0,0.45) !important; }

  .sd-th {
    padding: 11px 16px; text-align: left;
    font-size: 9px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--nd-tx30); font-family: 'DM Sans', sans-serif;
    border-bottom: 1px solid var(--nd-w08); white-space: nowrap; background: var(--nd-surface-2);
  }
  .sd-tr { border-bottom: 1px solid var(--nd-w05); transition: background 0.15s; }
  .sd-tr:last-child { border-bottom: none; }
  .sd-tr:hover td { background: var(--nd-w03); }
  .sd-td { padding: 13px 16px; vertical-align: middle; }

  .sd-month-input {
    padding: 8px 12px; background: var(--nd-w06); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-text); font-size: 12px; font-family: 'DM Sans', sans-serif;
    outline: none; cursor: pointer; transition: border-color 0.15s;
    color-scheme: dark;
  }
  .sd-month-input:focus { border-color: rgba(96,165,250,0.5); }

  .sd-toggle-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: 8px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 700;
    border: none; transition: all 0.18s;
  }
  .sd-toggle-show { background: linear-gradient(135deg,#3B82F6,#22D3A5); color: var(--nd-text-strong); box-shadow: 0 4px 12px rgba(59,130,246,0.28); }
  .sd-toggle-hide { background: rgba(248,113,113,0.15); color: #F87171; border: 1px solid rgba(248,113,113,0.28); }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--nd-w10); border-radius: 4px; }
`;

// ── Dark chart tooltip ────────────────────────────────────────────
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--nd-surface-2)', border: '1px solid var(--nd-w12)',
      borderRadius: 10, padding: '12px 16px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
    }}>
      <div style={{ fontWeight: 700, color: 'var(--nd-text)', fontSize: 13, marginBottom: 8 }}>
        {payload[0].payload.name}
      </div>
      <div style={{ color: '#34D399', fontSize: 12, fontWeight: 600, marginBottom: 4, fontFamily: "'DM Mono',monospace" }}>
        Rs {Number(payload[0]?.value || 0).toLocaleString()}
      </div>
      <div style={{ color: '#60A5FA', fontSize: 12, fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>
        {payload[1]?.value} orders
      </div>
    </div>
  );
};

// ── Ultra-Safe Amount Parser ──────────────────────────────────────
const getSafeAmount = (order) => {
  if (!order) return 0;
  
  // Try all possible object keys an API might send
  const rawValue = order.final_amount ?? order.amount ?? order.total_amount ?? order.total ?? order.grand_total ?? order.price ?? 0;
  
  if (typeof rawValue === 'number') return rawValue;
  
  // Remove EVERYTHING except digits, decimals, and minus signs (e.g., "Rs 5,000.50" => "5000.50")
  const cleanStr = String(rawValue).replace(/[^0-9.-]+/g, '');
  const parsed = parseFloat(cleanStr);
  
  return isNaN(parsed) ? 0 : parsed;
};

// ══════════════════════════════════════════════════════════════════
//   MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const SalesDashboard = () => {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [details, setDetails]       = useState([]);
  const [salesData, setSalesData]   = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const authHeader = getAuthHeader();

  useEffect(() => {
    fetchAllSales();
  }, [month]);

  const fetchAllSales = async () => {
    try {
      // ✅ FIX 1: Exact Start and End Date calculation (e.g., 2026-01-01 to 2026-01-31)
      const [year, monthNum] = month.split('-');
      const start = `${year}-${monthNum}-01`;
      
      // new Date(year, monthNum, 0) gives the LAST day of the selected month
      const lastDay = new Date(year, parseInt(monthNum), 0).getDate();
      const end = `${year}-${monthNum}-${String(lastDay).padStart(2, '0')}`;
      
      let allOrders = [];
      
      const firstRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/sales/sales-orders`, {
        ...authHeader, params: { startDate: start, endDate: end, page: 1 }
      });
      
      // ✅ FIX 2: Check multiple array paths in response gracefully
      let fetchedData = firstRes.data?.data || firstRes.data?.orders || firstRes.data?.sales;
      if (!Array.isArray(fetchedData)) {
        fetchedData = Array.isArray(firstRes.data) ? firstRes.data : [];
      }
      
      if (fetchedData.length > 0) {
        allOrders = [...fetchedData];
        const totalPages = firstRes.data?.totalPages || 1;
        
        if (totalPages > 1) {
          for (let i = 2; i <= totalPages; i += 5) {
            const chunk = [];
            for (let j = i; j < i + 5 && j <= totalPages; j++) {
              chunk.push(axios.get(`${process.env.REACT_APP_API_BASE_URL}/sales/sales-orders`, {
                ...authHeader, params: { startDate: start, endDate: end, page: j }
              }));
            }
            const results = await Promise.all(chunk);
            results.forEach(r => {
              let chunkData = r.data?.data || r.data?.orders || r.data?.sales;
              if (!Array.isArray(chunkData)) chunkData = Array.isArray(r.data) ? r.data : [];
              allOrders = [...allOrders, ...chunkData];
            });
          }
        }
      }
      
      // ✅ FIX 3: Robust mapping and cleaning
      const formattedDetails = allOrders.map(o => ({
        order_id: o.id || o.order_id,
        customer_id: o.cid || o.customer_id,
        client_name: o.client_name || o.customer_name || 'Walk-in',
        phone: o.phone_number || o.phone || '—',
        amount: getSafeAmount(o), 
        is_paid: o.is_paid || o.status
      }));
      
      setDetails(formattedDetails);
      
      // Map agent stats
      const agentMap = {};
      allOrders.forEach(o => {
        const agent = o.agent_name || o.agent || 'Unknown Agent';
        if (!agentMap[agent]) {
          agentMap[agent] = { agent_name: agent, total_orders: 0, total_sales: 0 };
        }
        agentMap[agent].total_orders += 1;
        agentMap[agent].total_sales += getSafeAmount(o);
      });
      
      const formattedSalesData = Object.values(agentMap).sort((a, b) => b.total_sales - a.total_sales);
      setSalesData(formattedSalesData);
      
    } catch (error) {
      console.error('Failed to fetch sales:', error);
      setDetails([]);
      setSalesData([]);
    }
  };

  const stats = useMemo(() => {
    let paidAmt = 0, unpaidAmt = 0, paidCnt = 0, unpaidCnt = 0;
    if (!details || !Array.isArray(details))
      return { paidAmount: 0, unpaidAmount: 0, paidCount: 0, unpaidCount: 0, totalAmount: 0, totalOrders: 0 };
    
    details.forEach(order => {
      const amount = Number(order.amount) || 0;
      
      // ✅ Broad Paid Status Check
      const statusStr = String(order.is_paid || '').toLowerCase().trim();
      const isPaid = (
        order.is_paid === true || 
        order.is_paid === 1 || 
        statusStr === '1' || 
        statusStr === 'true' || 
        statusStr === 'yes' || 
        statusStr === 'paid' ||
        statusStr === 'completed'
      );
      
      if (isPaid) {
        paidAmt += amount; paidCnt++;
      } else {
        unpaidAmt += amount; unpaidCnt++;
      }
    });
    
    return { 
      paidAmount: paidAmt, 
      unpaidAmount: unpaidAmt, 
      paidCount: paidCnt, 
      unpaidCount: unpaidCnt, 
      totalAmount: paidAmt + unpaidAmt, 
      totalOrders: paidCnt + unpaidCnt 
    };
  }, [details]);

  const chartData = [
    { name: 'Paid',   amount: stats.paidAmount,  orders: stats.paidCount   },
    { name: 'Unpaid', amount: stats.unpaidAmount, orders: stats.unpaidCount },
  ];

  const formatMonthDisplay = (m) => {
    if (!m) return '';
    return new Date(m + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const paidPct = stats.totalAmount > 0
    ? Math.round((stats.paidAmount / stats.totalAmount) * 100)
    : 0;

  const agentTotal = {
    orders: salesData.reduce((s, a) => s + (parseInt(a.total_orders) || 0), 0),
    sales:  salesData.reduce((s, a) => s + (parseFloat(a.total_sales) || 0), 0),
  };

  // ─────────────────────────────────────────────────────────────
  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: '14px 20px', minHeight: '100vh' }}>
      <style>{STYLES}</style>

      {/* ── PAGE HEADER ─────────────────────────────────────────── */}
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 16, marginBottom: 18, overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 24px',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: 14,
        }}>
          <div>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 5 }}>
              iNOVAA · SALES ANALYTICS
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>
              Sales Dashboard
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>
              {formatMonthDisplay(month)} — Performance Overview
            </div>
          </div>

          {/* Month picker */}
          <div>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>
              Select Month
            </div>
            <input
              type="month"
              className="sd-month-input"
              value={month}
              onChange={e => setMonth(e.target.value)}
            />
          </div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {/* ── STAT CARDS ──────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 14, marginBottom: 18 }}>

        {/* Total Sales */}
        <div className="sd-stat-card" style={{
          background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 14, padding: '18px 20px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          position: 'relative', overflow: 'hidden', animationDelay: '0s',
        }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg,${T.purple},transparent)`, borderRadius: '14px 0 0 14px' }} />
          <div style={{ position: 'absolute', top: 0, right: 0, width: 70, height: 70, borderRadius: '0 14px 0 70px', background: `${T.purple}0C` }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Total Sales</div>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `${T.purple}18`, border: `1px solid ${T.purple}35`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp size={14} color={T.purple} />
            </div>
          </div>
          <div style={{ fontSize: 26, fontWeight: 900, color: T.purple, fontFamily: T.mono, letterSpacing: '-0.5px', lineHeight: 1, marginBottom: 5 }}>
            Rs {Number(stats.totalAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>
            {stats.totalOrders} orders this month
          </div>
        </div>

        {/* Paid Sales */}
        <div className="sd-stat-card" style={{
          background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 14, padding: '18px 20px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          position: 'relative', overflow: 'hidden', animationDelay: '0.06s',
        }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg,${T.green},transparent)`, borderRadius: '14px 0 0 14px' }} />
          <div style={{ position: 'absolute', top: 0, right: 0, width: 70, height: 70, borderRadius: '0 14px 0 70px', background: `${T.green}0C` }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Paid Sales</div>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `${T.green}18`, border: `1px solid ${T.green}35`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle size={14} color={T.green} />
            </div>
          </div>
          <div style={{ fontSize: 26, fontWeight: 900, color: T.green, fontFamily: T.mono, letterSpacing: '-0.5px', lineHeight: 1, marginBottom: 5 }}>
            Rs {Number(stats.paidAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{ flex: 1, height: 3, background: 'var(--nd-w08)', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${paidPct}%`, background: `linear-gradient(90deg,${T.green},${T.teal})`, borderRadius: 99, transition: 'width 0.6s ease' }} />
            </div>
            <span style={{ fontSize: 10, color: T.green, fontFamily: T.mono, fontWeight: 700 }}>{paidPct}%</span>
          </div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{stats.paidCount} orders completed</div>
        </div>

        {/* Unpaid Sales */}
        <div className="sd-stat-card" style={{
          background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 14, padding: '18px 20px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          position: 'relative', overflow: 'hidden', animationDelay: '0.12s',
        }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg,${T.amber},transparent)`, borderRadius: '14px 0 0 14px' }} />
          <div style={{ position: 'absolute', top: 0, right: 0, width: 70, height: 70, borderRadius: '0 14px 0 70px', background: `${T.amber}0C` }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Unpaid Sales</div>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `${T.amber}18`, border: `1px solid ${T.amber}35`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={14} color={T.amber} />
            </div>
          </div>
          <div style={{ fontSize: 26, fontWeight: 900, color: T.amber, fontFamily: T.mono, letterSpacing: '-0.5px', lineHeight: 1, marginBottom: 5 }}>
            Rs {Number(stats.unpaidAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>{stats.unpaidCount} orders pending</div>
        </div>
      </div>

      {/* ── CHART CARD ──────────────────────────────────────────── */}
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 14, marginBottom: 18, overflow: 'hidden',
      }}>
        {/* section head */}
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BarChart2 size={14} color={T.blue} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Sales Breakdown Chart</span>
          </div>
          <button
            className={`sd-toggle-btn ${showDetails ? 'sd-toggle-hide' : 'sd-toggle-show'}`}
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? <EyeOff size={13} /> : <Eye size={13} />}
            {showDetails ? 'Hide' : 'Show'} Order Details
          </button>
        </div>

        {/* recharts */}
        <div style={{ padding: '24px 20px' }}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} barGap={8}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--nd-w05)" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fill: 'var(--nd-tx40)', fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}
                axisLine={{ stroke: 'var(--nd-w08)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'var(--nd-tx30)', fontSize: 11, fontFamily: "'DM Mono',monospace" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59,130,246,0.06)' }} />
              <Legend
                wrapperStyle={{ fontSize: 12, fontWeight: 600, color: 'var(--nd-tx45)', fontFamily: "'DM Sans',sans-serif", paddingTop: 12 }}
                iconType="circle"
              />
              <Bar dataKey="amount" fill={T.teal}  name="Amount (Rs)" radius={[8,8,0,0]} barSize={56} />
              <Bar dataKey="orders" fill={T.blue}  name="Orders"       radius={[8,8,0,0]} barSize={56} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── ORDER DETAILS TABLE (toggle) ─────────────────────────── */}
      {showDetails && (
        <div style={{
          background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 14, marginBottom: 18, overflow: 'hidden',
          animation: 'fadeUp 0.25s ease both',
        }}>
          {/* section head */}
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(34,211,165,0.12)', border: '1px solid rgba(34,211,165,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <List size={14} color={T.teal} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Monthly Orders Detail</span>
            <span style={{ fontSize: 10, color: T.muted, fontFamily: T.mono, marginLeft: 2 }}>
              {details.length} records
            </span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th className="sd-th">Order ID</th>
                  <th className="sd-th">Customer</th>
                  <th className="sd-th">Phone</th>
                  <th className="sd-th">Amount</th>
                  <th className="sd-th" style={{ textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {details.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '48px 20px', textAlign: 'center', color: T.muted, fontSize: 13 }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                      No orders found for this month.
                    </td>
                  </tr>
                ) : (
                  details.map(order => {
                    const statusStr = String(order.is_paid || '').toLowerCase().trim();
                    const paid = order.is_paid === true || order.is_paid === 1 || statusStr === '1' || statusStr === 'true' || statusStr === 'yes' || statusStr === 'paid' || statusStr === 'completed';
                    
                    return (
                      <tr key={order.order_id} className="sd-tr">
                        <td className="sd-td">
                          <span style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 700, color: T.blue }}>
                            #{order.order_id}
                          </span>
                        </td>
                        <td className="sd-td">
                          <div style={{ fontWeight: 600, color: T.text, fontSize: 13, marginBottom: 2 }}>
                            {order.client_name}
                          </div>
                          <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>
                            ID: {order.customer_id}
                          </div>
                        </td>
                        <td className="sd-td">
                          <span style={{ fontSize: 12, color: T.muted, fontFamily: T.mono }}>
                            {order.phone}
                          </span>
                        </td>
                        <td className="sd-td">
                          <span style={{ fontSize: 14, fontWeight: 800, color: T.teal, fontFamily: T.mono }}>
                            Rs {Number(order.amount || 0).toLocaleString()}
                          </span>
                        </td>
                        <td className="sd-td" style={{ textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '3px 10px', borderRadius: 6,
                            fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', fontFamily: T.mono,
                            background: paid ? 'rgba(52,211,153,0.12)' : 'rgba(251,191,36,0.12)',
                            color:      paid ? T.green : T.amber,
                            border:     `1px solid ${paid ? 'rgba(52,211,153,0.28)' : 'rgba(251,191,36,0.28)'}`,
                          }}>
                            {paid ? '✓ Paid' : '⏳ Unpaid'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── AGENT SALES TABLE ────────────────────────────────────── */}
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 14, overflow: 'hidden',
      }}>
        {/* section head */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Users size={14} color={T.purple} />
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Monthly Sales by Agent</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th className="sd-th" style={{ width: 50 }}>#</th>
                <th className="sd-th">Agent Name</th>
                <th className="sd-th" style={{ textAlign: 'center' }}>Total Orders</th>
                <th className="sd-th" style={{ textAlign: 'right' }}>Total Sales</th>
              </tr>
            </thead>
            <tbody>
              {salesData.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: '48px 20px', textAlign: 'center', color: T.muted, fontSize: 13 }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                    No sales data available for this month.
                  </td>
                </tr>
              ) : (
                <>
                  {salesData.map((agent, index) => (
                    <tr key={index} className="sd-tr">
                      <td className="sd-td">
                        <span style={{ fontSize: 12, color: T.muted, fontFamily: T.mono, fontWeight: 600 }}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="sd-td">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                            background: 'linear-gradient(135deg,#3B82F6,#A78BFA)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--nd-text-strong)', fontWeight: 800, fontSize: 14,
                            boxShadow: '0 3px 10px rgba(0,0,0,0.30)',
                          }}>
                            {agent.agent_name ? agent.agent_name.charAt(0).toUpperCase() : 'U'}
                          </div>
                          <span style={{ fontWeight: 600, color: T.text, fontSize: 13 }}>
                            {agent.agent_name}
                          </span>
                        </div>
                      </td>
                      <td className="sd-td" style={{ textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block', padding: '3px 12px', borderRadius: 6,
                          background: 'rgba(59,130,246,0.12)', color: '#60A5FA',
                          border: '1px solid rgba(59,130,246,0.25)',
                          fontSize: 12, fontWeight: 700, fontFamily: T.mono,
                        }}>
                          {parseInt(agent.total_orders).toLocaleString()}
                        </span>
                      </td>
                      <td className="sd-td" style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: T.teal, fontFamily: T.mono }}>
                          Rs {Number(agent.total_sales || 0).toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  ))}

                  {/* Totals row */}
                  <tr style={{ background: T.surf2, borderTop: `1px solid ${T.bord2}` }}>
                    <td colSpan={2} className="sd-td">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <div style={{ width: 5, height: 5, borderRadius: '50%', background: `linear-gradient(135deg,${T.blue},${T.teal})` }} />
                        <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Total</span>
                      </div>
                    </td>
                    <td className="sd-td" style={{ textAlign: 'center' }}>
                      <span style={{ fontSize: 14, fontWeight: 900, color: T.blue, fontFamily: T.mono }}>
                        {Number(agentTotal.orders || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="sd-td" style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 16, fontWeight: 900, color: T.teal, fontFamily: T.mono }}>
                        Rs {Number(agentTotal.sales || 0).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;